import type {
  AddTeamMembersFailedItem,
  AddTeamMembersPayload,
  ActiveUser,
  Team,
} from "../../../../../api/teams";
import { TEAM_STATUS, type TeamStatus } from "../../../../../api/teams/teams.types";
import type { UserRoles } from "../../../../../shared/constants/common.constant";
import type { ProblemDetails } from "../../../../../shared/types/problem-details.type";
import {
  TEAM_ROLE_OPTIONS,
  USER_DELETION_IN_PROGRESS_ERROR_TYPE,
} from "./team.constants";

const isDeletingStatus = (status: Team["status"]): boolean => {
  if (!status) {
    return false;
  }

  const normalized = status.trim().toLowerCase();
  return normalized === "deleting" || normalized === "deleted";
};

const isMovingDataStatus = (status: Team["status"]): boolean => {
  if (!status) {
    return false;
  }

  const normalized = status.trim().toLowerCase();
  return (
    normalized === "movingdata" ||
    normalized === "moving_data" ||
    normalized === "datamoved" ||
    normalized === "data_moved"
  );
};

export const getSelectableTeams = (
  teams: Team[],
  excludeTeamId?: string
): Team[] =>
  teams.filter(
    (team) =>
      !isTeamDeletionInProgress(team) &&
      (excludeTeamId === undefined || team.id !== excludeTeamId)
  );

export const getDefaultTeamId = (teams: Team[]): string =>
  teams.find((team) => team.isDefault)?.id ?? teams[0]?.id ?? "";

export const isTeamDeletionInProgress = (team: Team): boolean =>
  team.isDeletionInProgress === true ||
  team.isDataMovementInProgress === true ||
  isDeletingStatus(team.status) ||
  isMovingDataStatus(team.status);

export const isTeamMovingData = (team: Team): boolean =>
  team.isDataMovementInProgress === true || isMovingDataStatus(team.status);

const MOVING_PROFILE_COUNTS_STORAGE_KEY = "kycp-teams-moving-profile-counts";

const readMovingProfileCounts = (): Record<string, number> => {
  try {
    const stored = sessionStorage.getItem(MOVING_PROFILE_COUNTS_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Record<string, number>) : {};
  } catch {
    return {};
  }
};

export const getStoredMovingProfileCount = (
  teamId: string
): number | undefined => readMovingProfileCounts()[teamId];

export const setStoredMovingProfileCount = (
  teamId: string,
  count: number
): void => {
  sessionStorage.setItem(
    MOVING_PROFILE_COUNTS_STORAGE_KEY,
    JSON.stringify({ ...readMovingProfileCounts(), [teamId]: count })
  );
};

export const removeStoredMovingProfileCounts = (teamIds: string[]): void => {
  if (teamIds.length === 0) {
    return;
  }

  const counts = readMovingProfileCounts();
  teamIds.forEach((teamId) => {
    delete counts[teamId];
  });
  sessionStorage.setItem(
    MOVING_PROFILE_COUNTS_STORAGE_KEY,
    JSON.stringify(counts)
  );
};

export const getTeamMovingProfileCount = (
  team: Pick<Team, "profilesCount">,
  movingProfileCount?: number
): number => team.profilesCount ?? movingProfileCount ?? 0;

export const withMovingProfileCount = (team: Team): Team => {
  if (!isTeamMovingData(team)) {
    return team;
  }

  const storedCount = getStoredMovingProfileCount(team.id);
  const profilesCount =
    team.profilesCount && team.profilesCount > 0
      ? team.profilesCount
      : storedCount;

  return profilesCount !== undefined ? { ...team, profilesCount } : team;
};

export interface PendingTeamDeletion {
  status: TeamStatus;
  profilesCount?: number;
}

export const mergeTeamWithPendingDeletion = (
  team: Team,
  pending?: PendingTeamDeletion
): Team => {
  if (!pending) {
    return team;
  }

  const isDataMovement = pending.status === TEAM_STATUS.MovingData;

  return {
    ...team,
    status: pending.status,
    isDeletionInProgress: true,
    isDataMovementInProgress: isDataMovement,
    profilesCount: isDataMovement
      ? (pending.profilesCount ?? team.profilesCount)
      : team.profilesCount,
  };
};

export const isTeamNameConflictError = (error: unknown): boolean => {
  const problemDetails = error as ProblemDetails;
  return (
    problemDetails.status === 409 || problemDetails.details?.status === 409
  );
};

export const getUserInitials = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0]?.[0] ?? ""}${parts.at(-1)?.[0] ?? ""}`.toUpperCase();
};

export const excludeUsersByIds = (
  users: ActiveUser[],
  excludeUserIds: number[]
): ActiveUser[] => {
  if (excludeUserIds.length === 0) {
    return users;
  }
  const excludeSet = new Set(excludeUserIds);
  return users.filter((user) => !excludeSet.has(user.userId));
};

export const filterActiveUsersBySearch = (
  users: ActiveUser[],
  searchTerm: string
): ActiveUser[] => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) {
    return users;
  }
  return users.filter(
    (user) =>
      user.fullName.toLowerCase().includes(normalizedSearch) ||
      user.userId.toString().includes(normalizedSearch)
  );
};

export const allSelectedUsersHaveRoles = (
  selectedUsers: Map<number, number | "">
): boolean => {
  if (selectedUsers.size === 0) {
    return true;
  }

  let allHaveRoles = true;
  selectedUsers.forEach((roleId) => {
    if (roleId === "") {
      allHaveRoles = false;
    }
  });

  return allHaveRoles;
};

export const mapSelectedUsersToAddTeamUsers = (
  selectedUsers: Map<number, number | "">,
  excludeUserIds: number[] = []
): AddTeamMembersPayload => {
  const excludeSet = new Set(excludeUserIds);
  const users: AddTeamMembersPayload = [];

  selectedUsers.forEach((roleId, userId) => {
    if (roleId === "" || excludeSet.has(userId)) {
      return;
    }
    users.push({ userId, roleId });
  });

  return users;
};

export const getAddMembersFailureMessage = (
  failed: AddTeamMembersFailedItem[],
  activeUsersById: Map<number, ActiveUser>,
  translate: (
    _key: string,
    _options?: {
      userName?: string;
    }
  ) => string
): string => {
  const recentlyRemovedItem = failed.find(
    (item) => item.error?.type === USER_DELETION_IN_PROGRESS_ERROR_TYPE
  );

  if (recentlyRemovedItem) {
    const userName =
      activeUsersById.get(recentlyRemovedItem.failedItem.userId)?.fullName ?? "";

    return (
      translate("settings:teams.errors.userRecentlyRemoved", {
        userName,
      }) ?? ""
    );
  }

  return translate("settings:teams.errors.addMembersFailed") ?? "";
};

export const formatTeamDescription = (description?: string | null): string => {
  const normalized =
    typeof description === "string" ? description.trim() : "";
  return normalized.length > 0 ? normalized : "-";
};

export const getTeamRoleLabel = (
  roleId: number,
  translate: (_key: string) => string
): string => {
  const role = TEAM_ROLE_OPTIONS.find((option) => option.id === roleId);
  return role ? translate(role.labelKey) : "";
};

export const getTeamRoleLabelByName = (
  roleName: UserRoles,
  translate: (_key: string) => string
): string => {
  const role = TEAM_ROLE_OPTIONS.find((option) => option.name === roleName);
  return role ? translate(`settings:${role.labelKey}`) : roleName;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const substituteTeamIdWithNameInMessage = (
  message: string,
  teamId: string,
  teamName: string
): string => {
  if (
    teamId.length === 0 ||
    typeof teamName !== "string" ||
    teamName.length === 0
  ) {
    return message;
  }

  if (!message.includes(teamId)) {
    return message;
  }

  const teamIdWrappedPattern = new RegExp(
    `\\bteam\\s*\\(\\s*${escapeRegExp(teamId)}\\s*\\)`,
    "gi"
  );

  let result = message.replace(teamIdWrappedPattern, teamName);
  if (result.includes(teamId)) {
    result = result.split(teamId).join(teamName);
  }

  const teamNameWrappedPattern = new RegExp(
    `\\bteam\\s*\\(\\s*${escapeRegExp(teamName)}\\s*\\)`,
    "gi"
  );
  result = result.replace(teamNameWrappedPattern, teamName);

  const teamNameAdjacentPattern = new RegExp(
    `\\bteam\\s+${escapeRegExp(teamName)}\\b`,
    "gi"
  );
  result = result.replace(teamNameAdjacentPattern, teamName);

  return result;
};

export const getPageAfterRemovingMembers = (
  currentPage: number,
  totalCount: number,
  removedCount: number,
  itemsPerPage: number
): number => {
  const remainingCount = totalCount - removedCount;
  if (remainingCount <= 0) {
    return 1;
  }

  const totalPages = Math.ceil(remainingCount / itemsPerPage);
  return Math.min(currentPage, totalPages);
};
